import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Header, List, Card, Button, Icon, Confirm } from "semantic-ui-react";
import "../styles/YoutubeSetupPage.less";
import YouTubeStore from "~/stores/YouTubeStore";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import NotAuthorizedPage from "~/components/element/NotAuthorizedPage";
import User from "~/models/User";

@observer
class YoutubeSetupPage extends Component {
  constructor(props) {
    super(props);
    document.title = "Youtube Setup";
    this.state = {
      youTubeAuthorizationUrl: null,
      youtubeDisconnectConfirmModal: false,
    };
    this.youtubeStore = new YouTubeStore();
  }

  componentDidMount() {
    const {
      user: { enableClientYoutubeSetup },
    } = this.props;

    if (!enableClientYoutubeSetup) {
      return;
    }

    this.youtubeStore.getUserInfo().then(() => {
      if (!this.youtubeStore.youtubeAuthentication) {
        this.youtubeStore.getLoginUrl().then(url => this.setState({ youTubeAuthorizationUrl: url }));
      }
    });
  }

  disconnect = () => {
    this.youtubeStore.removeAccountLink().then(() => {
      this.youtubeStore.getLoginUrl().then(url => this.setState({ youTubeAuthorizationUrl: url }));
    });
  };

  render() {
    const {
      user: { enableClientYoutubeSetup },
      t,
    } = this.props;

    if (!enableClientYoutubeSetup) {
      return <NotAuthorizedPage continueUrl="/tours" />;
    }

    if (this.youtubeStore.isLoading) {
      return <LoadingWrapper loading message={t("loading")} />;
    }

    const { youTubeAuthorizationUrl, youtubeDisconnectConfirmModal } = this.state;
    return (
      <>
        <div className="youtube-setup-outer-container">
          <div className="youtube-setup-header">
            <div className="youtube-setup-h1-wrap">
              <h1>{t("youtube:youtubeSetup")}</h1>
            </div>
          </div>
        </div>
        <div className="youtube-setup-outer-container">
          <Header as="h3">{t("youtube:automatedYoutubePublishing")}</Header>
          <p>{t("youtube:inOrderToAutomaticallyPublish")}</p>
        </div>
        <div className="youtube-setup-outer-container">
          <Header as="h3">{t("termsOfUseAndPolicy")}</Header>
          <p>
            {t("beforeProceedingReadOur")}{" "}
            <a target="_blank" rel="noopener noreferrer" href="/public/pages/privacy_policy">
              {t("termsOfUseAndPolicy")}
            </a>
          </p>
        </div>
        <div className="youtube-setup-outer-container">
          <Header as="h3">{t("youtube:dataCollection")}</Header>
          <List bulleted>
            <List.Item content={t("youtube:afterTheWholeProcessIsCompleted")} />
            <List.Item content={t("youtube:thisInformationIsOnlyDisplayed")} />
            <List.Item>
              {t("youtube:weWillAutomaticallyRefreshThisData")}{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://developers.google.com/youtube/terms/developer-policies#e.-handling-youtube-data-and-content"
              >
                {t("youtube:youtubeAPIUsagePolicy")}
              </a>
            </List.Item>
            <List.Item content={t("youtube:weWillOnlyUploadVideosGeneratedForTours")} />
            <List.Item content={t("youtube:weWontPerformAnyOtherAction")} />
            <List.Item content={t("youtube:weWillProvideDisconnectAccount")} />
            <List.Item>
              {t("youtube:youCanRevokeAccessAtAnyTime")}{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://security.google.com/settings/security/permissions"
              >
                {t("googleSecuritySettings")}
              </a>
            </List.Item>
          </List>
        </div>
        <div className="youtube-setup-outer-container">
          <Header as="h3">{t("youtube:youtubeAPIServices")}</Header>
          <List bulleted>
            <List.Item>
              {t("youtube:youtubeTermsAndConditions")}{" "}
              <a target="_blank" rel="noopener noreferrer" href="https://www.youtube.com/t/terms">
                https://www.youtube.com/t/terms
              </a>
            </List.Item>
            <List.Item>
              {t("googlePrivacyPolicy")}{" "}
              <a target="_blank" rel="noopener noreferrer" href=" http://www.google.com/policies/privacy">
                http://www.google.com/policies/privacy
              </a>
            </List.Item>
            <List.Item>
              {t("youtube:revokingAccessToYourYoutubeAccount")}{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://security.google.com/settings/security/permissions"
              >
                https://security.google.com/settings/security/permissions
              </a>
            </List.Item>
            <List.Item>
              {t("youtube:refreshingStoringDisplayingAPI")}{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://developers.google.com/youtube/terms/developer-policies#e.-handling-youtube-data-and-content"
              >
                https://developers.google.com/youtube/terms/developer-policies#e.-handling-youtube-data-and-content
              </a>
            </List.Item>
          </List>
        </div>
        <div className="youtube-setup-outer-container">
          <Header as="h3">{t("youtube:youtubeAccount")}</Header>
          {this.youtubeStore.youtubeAuthentication && (
            <Card>
              <Card.Content>
                <a
                  href={`https://youtube.com/channel/${this.youtubeStore.youtubeAuthentication.channelId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon className="right floated" color="red" name="youtube" size="big" />
                </a>
                <Card.Header>
                  {this.youtubeStore.youtubeAuthentication.username ? (
                    this.youtubeStore.youtubeAuthentication.username
                  ) : (
                    <Icon loading name="spinner" />
                  )}
                </Card.Header>
                <Card.Meta>
                  Last sync{" "}
                  {this.youtubeStore.youtubeAuthentication.syncedDate
                    ? this.youtubeStore.youtubeAuthentication.syncedDate.fromNow()
                    : "none"}
                </Card.Meta>
                <Card.Description />
              </Card.Content>
              <Card.Content extra>
                <div className="ui two buttons">
                  <Button basic color="red" onClick={() => this.setState({ youtubeDisconnectConfirmModal: true })}>
                    {t("youtube:disconnectAccount")}
                  </Button>
                  <Confirm
                    open={youtubeDisconnectConfirmModal}
                    header={t("youtube:disconnectQuestion")}
                    content={t("youtube:areYouSureDisconnectYoutubeAccount")}
                    confirmButton={t("yes")}
                    onConfirm={this.disconnect}
                    onCancel={() => this.setState({ youtubeDisconnectConfirmModal: false })}
                  />
                </div>
              </Card.Content>
            </Card>
          )}
          {youTubeAuthorizationUrl && !this.youtubeStore.youtubeAuthentication && (
            <Card>
              <Card.Content>
                <Icon className="right floated" color="red" name="youtube" size="big" />
                <Card.Header>{t("notConnected")}</Card.Header>
                <Card.Description />
              </Card.Content>
              <Card.Content extra>
                <div className="ui two buttons">
                  <Button basic as="a" href={youTubeAuthorizationUrl} target="_blank" rel="noreferrer" color="green">
                    {t("addAccountNow")}
                  </Button>
                </div>
              </Card.Content>
            </Card>
          )}
        </div>
      </>
    );
  }
}

YoutubeSetupPage.propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(YoutubeSetupPage);
